/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import { updateErrorReport } from 'api-client/admin';
import { ODSAlert, ODSButton, ODSGroup, ODSInput, ODSLabel, ODSModal } from 'bc-ods';
import type { FC } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

interface Props {
  message?: string;
  stackTrace?: string;
  reportId?: number;
  onComplete: (succeeded: boolean) => void;
  isOpen?: boolean;
  toggle?: () => void;
}

const messages = defineMessages({
  backLink: {
    id: 'ErrorReportForm.backLink',
    defaultMessage: 'Back',
  },
  instructions: {
    id: 'ErrorReportForm.instructions',
    defaultMessage:
      'Please describe the error you encountered in the text box below. Be as specific and detailed as possible.',
  },
  messagePlaceholder: {
    id: 'ErrorReportForm.messagePlaceholder',
    defaultMessage: 'Enter additional info\u2026',
  },
  reportedMessageLabel: {
    id: 'ErrorReportForm.reportedMessageLabel',
    defaultMessage: 'Additional info',
  },
  stackTrace: {
    id: 'ErrorReportForm.stackTrace',
    defaultMessage: 'Stack trace',
  },
  submit: {
    id: 'ErrorReportForm.submit',
    defaultMessage: 'Update report',
  },
  cancel: {
    id: 'ErrorReportForm.cancel',
    defaultMessage: 'Cancel',
  },
  title: {
    id: 'ErrorReportForm.title',
    defaultMessage: 'Add more information to the report',
  },
  errorMessage: {
    id: 'ErrorReportForm.errorMessage',
    defaultMessage: 'Error message',
  },
  onlyUnclass: {
    id: 'ErrorReportForm.onlyUnclass',
    defaultMessage: 'UNCLASSIFIED INFORMATION ONLY',
  },
});

interface FoldedSectionProps {
  children?: React.ReactNode;
  label: string;
  className?: string;
}

const FoldedSection: FC<FoldedSectionProps> = ({ label, children, className }) => {
  const [isFolded, setIsFolded] = useState(true);
  return (
    <ODSGroup
      className={cx('border rounded', className, { 'bg-light': !isFolded })}
      stacked
      spacing="none"
    >
      <ODSButton
        toolbar
        icon="chevron-right"
        fullWidth
        onClick={() => {
          setIsFolded(!isFolded);
        }}
        iconRotate={isFolded ? undefined : 90}
        css={{ justifyContent: 'start !important' }}
      >
        {label}
      </ODSButton>
      {!isFolded && children}
    </ODSGroup>
  );
};

const ErrorReportForm: FC<Props> = ({
  stackTrace = '',
  message = '',
  reportId = -1,
  onComplete,
  isOpen,
  toggle,
}) => {
  const [reportedDescription, setReportedDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const intl = useIntl();

  const onSubmit = async () => {
    setSubmitting(true);
    let succeeded = false;
    try {
      await updateErrorReport(reportId, reportedDescription);
      succeeded = true;
    } catch (error) {
      console.error(error);
    }
    setSubmitting(false);
    onComplete(succeeded);
  };

  return (
    <ODSModal
      isOpen={isOpen}
      title={<FormattedMessage {...messages.title} />}
      primary={<FormattedMessage {...messages.submit} />}
      secondary={<FormattedMessage {...messages.cancel} />}
      primaryDisabled={reportId === -1 || submitting}
      onClick={async () => {
        await onSubmit();
      }}
      icon="bug"
      toggle={toggle}
      warning
      size="lg"
      noSubmitOnEnter
    >
      <ODSGroup stacked spacing="lg">
        <div>
          <FormattedMessage {...messages.instructions} />
        </div>
        <ODSAlert color="warning" className="w-100">
          <FormattedMessage {...messages.onlyUnclass} />
        </ODSAlert>
        {!!message && (
          <ODSGroup stacked spacing="sm">
            <ODSLabel>
              <FormattedMessage {...messages.errorMessage} />
            </ODSLabel>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <div className="fw-bold">"{message}"</div>
          </ODSGroup>
        )}
        <ODSInput
          disabled={submitting}
          label={intl.formatMessage(messages.reportedMessageLabel)}
          type="textarea"
          placeholder={intl.formatMessage(messages.messagePlaceholder)}
          value={reportedDescription}
          onChange={(e) => {
            setReportedDescription(e.target.value);
          }}
          autoResize
          css={{ textarea: { minHeight: '20vh !important' } }}
        />
        {!!stackTrace && (
          <FoldedSection label={intl.formatMessage(messages.stackTrace)} className="w-100">
            <div
              data-testid="stack-trace-output"
              className="small px-3 py-2 w-100 text-secondary border-top overflow-auto"
              css={{
                whiteSpace: 'pre',
                maxHeight: 'max(20vh, 20rem)',
              }}
            >
              {stackTrace}
            </div>
          </FoldedSection>
        )}
      </ODSGroup>
    </ODSModal>
  );
};

export default ErrorReportForm;
